/**
 * shipmentShared.jsx
 *
 * Single source of truth for everything shared between ShipmentsTable
 * and PartialShipmentsTab (and any future shipment view).
 *
 * Exports:
 *  - Constants        : TABLE_HEADERS, ALL_STATUSES, EVENT_OPTIONS, EXCEPTION_OPTIONS,
 *                       statusBadgeClass, DISABLED_STATUSES, DEFAULT_ADVANCED_FILTERS
 *  - Utils            : makeKey, toBackendStatus, mapDisplayStatus,
 *                       buildFilterNode, countActiveFilters, formatShipmentRow,
 *                       formatShipmentDisplayText, ShipmentDisplayDetail, PO_SO_DISPLAY_LABELS
 *  - Hook             : useShipmentFilters
 *  - UI Components    : AdvancedFilterToolbar, AdvancedFilterModal,
 *                       ActiveFilterPills, FilterSection, LabeledInput,
 *                       DateRangeRow
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { SlidersHorizontal, X, RotateCcw } from "lucide-react";
import { FaRegCalendarAlt } from "react-icons/fa";
import { format } from "date-fns";
import { useSelector, useDispatch } from "react-redux";
import { setAdvancedFilters as setAdvancedFiltersAction } from "../../../../redux/slices/shipmentSlice";
import { DisplayDetail } from "../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";

// ─── Constants ────────────────────────────────────────────────────────────────

export const TABLE_HEADERS = [
  "Selection", "Action", "Shipment Number", "Office Code", "Status",
  "PO Number", "Sales Order", "Origin", "Destination", "Destination City",
  "Destination State", "Destination Country", "Destination Zip", "Carrier Name",
  "Carrier SCAC Code", "PRO Number", "Equipment Number",
  "Planned Pickup Date", "Planned Delivery Date","Ready for Pickup Date Time", "Actual Pickup Time",
  "Actual Delivery Time", "Pickup Appointment Time", "Invoice Number",
  "Invoice Status", "Pickup Location Reason Name", "Total Charges", "Mode",
  "Created Date", "Updated Date", "Payment Type",
];

export const ALL_STATUSES = [
  "NEW", "ASSIGNED", "UNASSIGNED", "ACCEPTED", "TENDERED",
  "IN-TRANSIT", "DELIVERED", "CANCELLED",
];

export const EVENT_OPTIONS = [
  "Ready For Pickup",
];

export const EXCEPTION_OPTIONS = [
  "Missing Pickup Appointments",
  "Missing Delivery Appointments",
  "Not Picked Up",
  "Not Delivered",
  "Missing POD",
  "Not Invoiced",
];

export const statusBadgeClass = {
  NEW:              "bg-blue-100    dark:bg-blue-900/40    text-black dark:text-blue-200",
  ASSIGNED:         "bg-amber-100   dark:bg-amber-900/40   text-black dark:text-amber-200",
  UNASSIGNED:       "bg-neutral-200 dark:bg-neutral-700/40 text-black dark:text-gray-200",
  ACCEPTED:         "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  TENDERED:         "bg-indigo-100  dark:bg-indigo-900/40  text-black dark:text-indigo-200",
  "READY FOR PICKUP":"bg-cyan-100   dark:bg-cyan-900/40    text-black dark:text-cyan-200",
  "IN-TRANSIT":     "bg-teal-100    dark:bg-teal-900/40    text-black dark:text-teal-200",
  ACTIVE:           "bg-teal-100    dark:bg-teal-900/40    text-black dark:text-teal-200",
  DELIVERED:        "bg-green-100   dark:bg-green-900/40   text-black dark:text-green-200",
  COMPLETED:        "bg-green-100   dark:bg-green-900/40   text-black dark:text-green-200",
  INVOICED:         "bg-purple-100  dark:bg-purple-900/40  text-black dark:text-purple-200",
  CANCELLED:        "bg-red-100     dark:bg-red-900/40     text-black dark:text-red-200",
  DEFAULT:          "bg-gray-100    dark:bg-gray-700/40    text-black dark:text-gray-200",
};

export const DISABLED_STATUSES = new Set(["CANCELLED", "CANCELED"]);

/** LGX-1016: Refresh Rates/Miles are blocked once a shipment is delivered (Completed) and invoiced. */
export const REFRESH_RATES_BLOCKED_MESSAGE =
  "Refreshing rates is blocked for Delivered shipments that are invoiced.";
export const REFRESH_MILES_BLOCKED_MESSAGE =
  "Refreshing miles is blocked for Delivered shipments that are invoiced.";
export const isRefreshBlockedForShipment = (shipmentDetails) =>
  Boolean(shipmentDetails?.isInvoiced) && shipmentDetails?.status === "Completed";

export const DEFAULT_ADVANCED_FILTERS = {
  statuses: [], origin: "", destination: "", carrierName: "",
  paymentTypes: [], modes: [],
  plannedPickupFrom: "", plannedPickupTo: "",
  plannedDeliveryFrom: "", plannedDeliveryTo: "",
  createdFrom: "", createdTo: "",
  exceptions: [],
};

/** Default status quick-filter (FilterButton) when no saved view is active. */
export const DEFAULT_QUICK_FILTER = "New";

export const quickFilterMatchesView = (saved, current) =>
  saved === undefined || (saved ?? null) === (current ?? null);

export const advancedFiltersMatchView = (saved, current) => {
  if (saved === undefined) return true;
  const a = saved ?? DEFAULT_ADVANCED_FILTERS;
  const b = current ?? DEFAULT_ADVANCED_FILTERS;
  return JSON.stringify(a) === JSON.stringify(b);
};

// ─── Utils ────────────────────────────────────────────────────────────────────

export const makeKey = (shipmentNumber, officeCode) =>
  `${shipmentNumber}::${officeCode}`;

export const toBackendStatus = (s) => {
  if (!s) return s;
  const u = s.toUpperCase();
  if (u === "IN-TRANSIT") return "ACTIVE";
  if (u === "DELIVERED")  return "COMPLETED";
  return u;
};

/** Any active advanced filter takes precedence over the quick-filter dropdown. */
export const resolveQuickStatusFilter = (quickFilter, advancedFilters) =>
  countActiveFilters(advancedFilters ?? DEFAULT_ADVANCED_FILTERS) > 0
    ? null
    : (quickFilter || null);

export const mapDisplayStatus = (status) => {
  if (!status) return "-";
  const s = status.toUpperCase();
  if (s === "ACTIVE")    return "IN-TRANSIT";
  if (s === "COMPLETED") return "DELIVERED";
  return s;
};

/** Replace commas with semicolons for multi-value PO / sales order display. */
export const formatShipmentDisplayText = (value) => {
  if (value == null || value === "") return value;
  if (typeof value === "string") {
    return value.includes(",") ? value.replace(/,/g, ";") : value;
  }
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (React.isValidElement(value)) return value;
  const text = String(value);
  return text.includes(",") ? text.replace(/,/g, ";") : value;
};

/** Labels for detail/header fields that should use comma → semicolon formatting. */
export const PO_SO_DISPLAY_LABELS = new Set([
  "PO Number",
  "PO Numbers",
  "Sales Order",
  "SO Number",
  "Sales Order Number",
  "Sales Order Numbers",
]);

const poSoCell = (value) => {
  if (value == null || value === "") return "-";
  return formatShipmentDisplayText(value);
};

/** Read-only detail field — formats PO / sales order values only. */
export const ShipmentDisplayDetail = ({
  value,
  label,
  isEditable = false,
  isReadOnly = false,
  ...props
}) => {
  const shouldFormat = label && PO_SO_DISPLAY_LABELS.has(label);
  const displayValue =
    isEditable && !isReadOnly
      ? value
      : shouldFormat
        ? formatShipmentDisplayText(value)
        : value;

  return (
    <DisplayDetail
      {...props}
      label={label}
      isEditable={isEditable}
      isReadOnly={isReadOnly}
      value={displayValue}
    />
  );
};

/** Formats sticky-header PO / SO fields for read-only shipment detail view. */
export const formatShipmentInfoDataForDisplay = (infoData = {}) =>
  Object.fromEntries(
    Object.entries(infoData).map(([key, field]) => {
      if (!field || typeof field !== "object") return [key, field];
      if (PO_SO_DISPLAY_LABELS.has(key) && typeof field.value === "string") {
        return [key, { ...field, value: formatShipmentDisplayText(field.value) }];
      }
      return [key, field];
    })
  );

// 30-minute grace window used by exception filters
const graceTimestamp = () =>
  new Date(Date.now() - 30 * 60 * 1000).toISOString().replace("Z", "");

/**
 * Builds a backend FilterNode tree from advanced filter state.
 * @param {object}  adv             - advanced filter values
 * @param {string}  topLevelStatus  - status from the active tab (e.g. "Active")
 */
export const buildFilterNode = (adv, topLevelStatus) => {
  const nodes = [];

  if (topLevelStatus && !adv.statuses?.length)
    nodes.push({ field: "status", op: "EQ", value: toBackendStatus(topLevelStatus) });

  if (adv.statuses.length === 1)
    nodes.push({ field: "status", op: "EQ", value: toBackendStatus(adv.statuses[0]) });
  else if (adv.statuses.length > 1)
    nodes.push({ field: "status", op: "IN", value: adv.statuses.map(toBackendStatus).join(",") });

  if (adv.origin?.trim())
    nodes.push({ field: "origin",       op: "CONTAINS", value: adv.origin.trim() });
  if (adv.destination?.trim())
    nodes.push({ field: "destination",  op: "CONTAINS", value: adv.destination.trim() });
  if (adv.carrierName?.trim())
    nodes.push({ field: "carrierName",  op: "CONTAINS", value: adv.carrierName.trim() });

  if (adv.paymentTypes.length === 1)
    nodes.push({ field: "paymentMethod", op: "EQ", value: adv.paymentTypes[0] });
  else if (adv.paymentTypes.length > 1)
    nodes.push({ field: "paymentMethod", op: "IN", value: adv.paymentTypes.join(",") });

  if (adv.modes.length === 1)
    nodes.push({ field: "movementType", op: "EQ", value: adv.modes[0] });
  else if (adv.modes.length > 1)
    nodes.push({ field: "movementType", op: "IN", value: adv.modes.join(",") });

  if (adv.plannedPickupFrom)   nodes.push({ field: "pickupDate",   op: "GTE", value: adv.plannedPickupFrom });
  if (adv.plannedPickupTo)     nodes.push({ field: "pickupDate",   op: "LTE", value: adv.plannedPickupTo });
  if (adv.plannedDeliveryFrom) nodes.push({ field: "deliveryDate", op: "GTE", value: adv.plannedDeliveryFrom });
  if (adv.plannedDeliveryTo)   nodes.push({ field: "deliveryDate", op: "LTE", value: adv.plannedDeliveryTo });
  if (adv.createdFrom)         nodes.push({ field: "createdDate",  op: "GTE", value: adv.createdFrom });
  if (adv.createdTo)           nodes.push({ field: "createdDate",  op: "LTE", value: adv.createdTo });

  const exceptionNodes = (adv.exceptions || []).flatMap((ex) => {
    const g = graceTimestamp();
    switch (ex) {
      case "Missing Pickup Appointments":
        return [{ operator: "AND", filters: [
          { field: "pickupAppointmentStartDate", op: "IS_NULL" },
          { field: "status", op: "EQ", value: "Accepted" },
        ]}];
      case "Missing Delivery Appointments":
        return [{ operator: "AND", filters: [
          { field: "deliveryAppointmentStartDate", op: "IS_NULL" },
          { field: "status", op: "EQ", value: "Accepted" },
        ]}];
      case "Ready For Pickup":
        // UI: Events section. Backend: exception predicate — expands IS_NOT_NULL
        // into RFP-set + PICKUP gate in/out empty.
        return [{ field: "readyForPickupDateTime", op: "IS_NOT_NULL" }];
      case "Not Picked Up":
        return [{ operator: "AND", filters: [
          { field: "actualGateIn",               op: "IS_NULL" },
          { field: "pickupAppointmentStartDate", op: "IS_NOT_NULL" },
          { field: "pickupDate",                 op: "LTE", value: g },
        ]}];
      case "Not Delivered":
        return [{ operator: "AND", filters: [
          { field: "status",                      op: "NEQ",      value: "Completed" },
          { field: "actualGateOut",               op: "IS_NULL" },
          { field: "deliveryAppointmentStartDate",op: "IS_NOT_NULL" },
          { field: "deliveryDate",                op: "LTE", value: g },
        ]}];
      case "Missing POD":
        return [{ field: "missingPod",  op: "IS_TRUE" }];
      case "Not Invoiced":
        return [{ field: "isInvoiced",  op: "IS_FALSE" }];
      default:
        return [];
    }
  });

  if (exceptionNodes.length === 1) nodes.push(exceptionNodes[0]);
  else if (exceptionNodes.length > 1) nodes.push({ operator: "OR", filters: exceptionNodes });

  if (nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0];
  return { operator: "AND", filters: nodes };
};

/**
 * Builds the POST body for the shipment search endpoints.
 *
 * Lives here because ShipmentsTable and PartialShipmentsTab (Drop Trailers) each used to
 * assemble it inline, and they drifted: the shipments grid learned to drop the quick-filter
 * status while searching, Drop Trailers did not, so searching there only ever looked inside
 * the selected tab status (which defaults to "New") and found nothing the rest of the time.
 *
 * The rule, in one place: a typed query is a request for one specific shipment, so neither
 * the quick status nor the advanced filters narrow it.
 *
 * @param {string}  searchQuery           - raw search box text ("" when not searching)
 * @param {string}  filter                - quick-filter status from the tab
 * @param {object}  advancedFilters       - advanced filter state
 * @param {string}  sortBy                - backend sort key, or null
 * @param {boolean} ascending             - sort direction
 * @param {string}  filterType            - ALL | DROP_TRAILER | NOT_INVOICED
 * @param {boolean} includeAdvancedFilters - false for views with no filter UI
 */
export const buildShipmentSearchPayload = ({
  searchQuery,
  filter,
  advancedFilters,
  sortBy = null,
  ascending = false,
  filterType,
  includeAdvancedFilters = true,
}) => {
  const query = (searchQuery || "").trim() || null;
  const isSearching = !!query;

  const quickStatus = isSearching
    ? null
    : resolveQuickStatusFilter(filter, advancedFilters);

  const filterNode = (includeAdvancedFilters && !isSearching)
    ? buildFilterNode(advancedFilters, quickStatus)
    : null;

  return {
    query,
    sortBy,
    filterType,
    ascending,
    status: quickStatus ? toBackendStatus(quickStatus) : null,
    ...(filterNode ? { filter: filterNode } : {}),
  };
};

export const countActiveFilters = (f) => {
  const selectedEvents = (f.exceptions || []).filter((ex) => EVENT_OPTIONS.includes(ex));
  const selectedExceptions = (f.exceptions || []).filter((ex) => !EVENT_OPTIONS.includes(ex));
  let n = 0;
  if (f.statuses.length)                          n++;
  if (selectedEvents.length)                      n++;
  if (selectedExceptions.length)                  n++;
  if (f.origin.trim())                            n++;
  if (f.destination.trim())                       n++;
  if (f.carrierName.trim())                       n++;
  if (f.paymentTypes.length)                      n++;
  if (f.modes.length)                             n++;
  if (f.plannedPickupFrom  || f.plannedPickupTo)  n++;
  if (f.plannedDeliveryFrom|| f.plannedDeliveryTo)n++;
  if (f.createdFrom        || f.createdTo)        n++;
  return n;
};

/**
 * Formats a raw API shipment object into a display-ready table row map.
 *
 * Handles two data shapes transparently:
 *  - ShipmentsTable shape : { totalCharge, appointmentTime, paymentMethod, ... }
 *  - PartialShipmentsTab  : { charges[], pickupAppointmentStartDate, freightPaymentTermCode, ... }
 */
export const formatShipmentRow = (shipment) => {
  const totalCharges =
    shipment?.totalCharge != null
      ? Number(shipment.totalCharge)
      : (shipment?.charges ?? []).reduce((sum, c) => sum + (Number(c?.amount) || 0), 0);

  // Support both field name conventions for appointment time
  const apptRaw = shipment?.pickupAppointmentStartDate ?? shipment?.appointmentTime;

  return {
    "Shipment Number":           shipment?.shipmentNumber ?? "-",
    "Office Code":               shipment?.officeCode ?? "-",
    "Status": (
      <span className={`text-black text-fluid-sm px-fluid-1.5 py-fluid-1 rounded font-medium ${
        statusBadgeClass[(shipment?.status || "").toUpperCase()] ?? statusBadgeClass.DEFAULT
      }`}>
        {mapDisplayStatus(shipment?.status)}
      </span>
    ),
    "PO Number":                 poSoCell(shipment?.poNumber),
    "Sales Order":               poSoCell(shipment?.salesOrderNumber),
    "Origin":                    shipment?.portOfOrigin ?? "-",
    "Destination":               shipment?.portOfDestination ?? "-",
    "Destination City":          shipment?.destinationCity ?? "-",
    "Destination State":         shipment?.destinationState ?? "-",
    "Destination Country":       shipment?.destinationCountry ?? "-",
    "Destination Zip":           shipment?.destinationZipCode ?? "-",
    "Carrier Name":              shipment?.carrierName ?? "-",
    "Carrier SCAC Code":         shipment?.carrierCode ?? "-",
    "PRO Number":                shipment?.proNumber ?? "-",
    "Equipment Number":          shipment?.equipmentNumber ?? "-",
    "Planned Pickup Date":       shipment?.pickupDate
      ? format(new Date(shipment.pickupDate),   "yyyy-MM-dd")        : "-",
    "Planned Delivery Date":     shipment?.deliveryDate
      ? format(new Date(shipment.deliveryDate), "yyyy-MM-dd")        : "-",
    "Ready for Pickup Date Time": shipment?.readyForPickupDateTime
      ? format(new Date(shipment.readyForPickupDateTime), "yyyy-MM-dd HH:mm") : "-",
    "Actual Pickup Time":        shipment?.actualGateIn
      ? format(new Date(shipment.actualGateIn), "yyyy-MM-dd HH:mm") : "-",
    "Actual Delivery Time":      shipment?.actualGateOut
      ? format(new Date(shipment.actualGateOut),"yyyy-MM-dd HH:mm") : "-",
    "Pickup Appointment Time":   apptRaw
      ? format(new Date(apptRaw),               "yyyy-MM-dd HH:mm") : "-",
    "Invoice Number":            shipment?.invoiceNumber ?? "-",
    "Invoice Status":            shipment?.invoiceNumber && shipment.invoiceNumber !== "-"
      ? (shipment?.invoiceStatus ?? "-")
      : "Not Invoiced",
    "Pickup Location Reason Name": shipment?.pickupLocationReasonName ?? "-",
    "Total Charges":             totalCharges ? `$${totalCharges.toFixed(2)}` : "-",
    "Mode":                      shipment?.movementType ?? "-",
    "Created Date":              shipment?.createdDate
      ? format(new Date(shipment.createdDate),  "yyyy-MM-dd HH:mm") : "-",
    "Updated Date":              shipment?.updatedDate
      ? format(new Date(shipment.updatedDate),  "yyyy-MM-dd HH:mm") : "-",
    // Support both field name conventions for payment type
    "Payment Type":              shipment?.freightPaymentTermCode ?? shipment?.paymentMethod ?? "-",
  };
};

// ─── Hook: useShipmentFilters ─────────────────────────────────────────────────

/**
 * Manages advanced filter state for a shipment view.
 *
 * @param {string}  stateKey  - unique key for this view's slice of state
 * @param {boolean} persist   - true  → Redux (survives tab switches, page refreshes)
 *                              false → component-local useState
 *
 * Both variants expose the same API so consumers are agnostic to the backing store.
 */
export const useShipmentFilters = ({ stateKey, persist = false }) => {
  const dispatch = useDispatch();

  // Always call useSelector — hooks cannot be called conditionally.
  // The value is only consumed when persist=true.
  const reduxFilters = useSelector(
    (s) => s.shipments.advancedFilters?.[stateKey] ?? DEFAULT_ADVANCED_FILTERS
  );
  const [localFilters, setLocalFilters] = useState(DEFAULT_ADVANCED_FILTERS);

  const filters = persist ? reduxFilters : localFilters;

  const updateFilters = useCallback(
    (newFilters) => {
      if (persist) {
        dispatch(setAdvancedFiltersAction({ stateKey, filters: newFilters }));
      } else {
        setLocalFilters(newFilters);
      }
    },
    [persist, stateKey, dispatch]
  );

  const clearFilters = useCallback(
    () => updateFilters(DEFAULT_ADVANCED_FILTERS),
    [updateFilters]
  );

  /** Removes a single filter pill by its key. */
  const removePill = useCallback(
    (key) => {
      const resets = {
        statuses:     { statuses: [] },
        events:       { exceptions: filters.exceptions.filter((ex) => !EVENT_OPTIONS.includes(ex)) },
        exceptions:   { exceptions: filters.exceptions.filter((ex) => EVENT_OPTIONS.includes(ex)) },
        origin:       { origin: "" },
        destination:  { destination: "" },
        carrierName:  { carrierName: "" },
        paymentTypes: { paymentTypes: [] },
        modes:        { modes: [] },
        pickup:       { plannedPickupFrom: "", plannedPickupTo: "" },
        delivery:     { plannedDeliveryFrom: "", plannedDeliveryTo: "" },
        created:      { createdFrom: "", createdTo: "" },
      };
      updateFilters({ ...filters, ...(resets[key] ?? {}) });
    },
    [filters, updateFilters]
  );

  return {
    filters,
    updateFilters,
    clearFilters,
    removePill,
    activeCount: countActiveFilters(filters),
  };
};

// ─── Primitive filter UI components ──────────────────────────────────────────

export const FilterSection = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-custom-bg-blue dark:text-gray-400 mb-2.5">
      {title}
    </p>
    {children}
  </div>
);

export const LabeledInput = ({ label, value, onChange, placeholder }) => (
  <div>
    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</p>
    <input
      type="text" value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
    />
  </div>
);

const DatePickerButton = ({ value, onChange, min }) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const formatted = value
    ? (([y, m, d]) => `${m} / ${d} / ${y}`)(value.split("-"))
    : "";

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className="relative flex items-center w-full px-3 py-1.5 text-sm rounded-lg border
        overflow-hidden border-gray-300 dark:border-acs-dark-border bg-white
        dark:bg-acs-dark-surface focus-within:ring-2 focus-within:ring-blue-500
        focus-within:border-blue-500 transition-colors cursor-pointer"
    >
      <span
        className={`flex-1 ${formatted ? "text-gray-900 dark:text-acs-dark-text" : "text-gray-400 dark:placeholder-gray-500"}
          ${focused ? "invisible" : ""}`}
      >
        {formatted || "mm / dd / yyyy"}
      </span>

      <FaRegCalendarAlt
        size={14}
        onClick={(e) => {
          e.stopPropagation(); // prevent wrapper's focus() from racing with showPicker()
          inputRef.current?.showPicker();
        }}
        className={`text-custom-bg-blue dark:text-acs-accent flex-shrink-0 cursor-pointer ${focused ? "invisible" : ""}`}
      />

      <input
        ref={inputRef}
        type="date"
        value={value}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`absolute left-0 top-0 h-full pl-3 bg-transparent outline-none
          text-gray-900 dark:text-acs-dark-text
          ${focused
            ? "opacity-100 w-[calc(100%+40px)] pointer-events-auto"
            : "opacity-0 w-full pointer-events-none"
          }`}
      />
    </div>
  );
};


export const DateRangeRow = ({ label, fromValue, toValue, onFromChange, onToChange }) => (
  <div className="grid grid-cols-[120px_1fr_1fr] items-center gap-3">
    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
    <DatePickerButton value={fromValue} onChange={onFromChange} />
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-400 flex-shrink-0">to</span>
      <div className="flex-1">
        <DatePickerButton value={toValue} onChange={onToChange} min={fromValue || undefined} />
      </div>
    </div>
  </div>
);

// ─── Active filter pills ──────────────────────────────────────────────────────

export const ActiveFilterPills = ({ filters, onRemove }) => {
  const selectedEvents = (filters.exceptions || []).filter((ex) => EVENT_OPTIONS.includes(ex));
  const selectedExceptions = (filters.exceptions || []).filter((ex) => !EVENT_OPTIONS.includes(ex));
  const pills = [
    filters.statuses.length     && { key: "statuses",    label: `Status: ${filters.statuses.join(", ")}` },
    selectedEvents.length       && { key: "events",      label: `Events: ${selectedEvents.join(", ")}` },
    selectedExceptions.length   && { key: "exceptions",  label: `Exceptions: ${selectedExceptions.join(", ")}` },
    filters.origin.trim()       && { key: "origin",      label: `Origin: ${filters.origin}` },
    filters.destination.trim()  && { key: "destination", label: `Destination: ${filters.destination}` },
    filters.carrierName.trim()  && { key: "carrierName", label: `Carrier: ${filters.carrierName}` },
    filters.paymentTypes.length && { key: "paymentTypes",label: `Payment: ${filters.paymentTypes.join(", ")}` },
    filters.modes.length        && { key: "modes",       label: `Mode: ${filters.modes.join(", ")}` },
    (filters.plannedPickupFrom || filters.plannedPickupTo) && {
      key: "pickup",
      label: `Pickup: ${filters.plannedPickupFrom || "..."} → ${filters.plannedPickupTo || "..."}`,
    },
    (filters.plannedDeliveryFrom || filters.plannedDeliveryTo) && {
      key: "delivery",
      label: `Delivery: ${filters.plannedDeliveryFrom || "..."} → ${filters.plannedDeliveryTo || "..."}`,
    },
    (filters.createdFrom || filters.createdTo) && {
      key: "created",
      label: `Created: ${filters.createdFrom || "..."} → ${filters.createdTo || "..."}`,
    },
  ].filter(Boolean);

  if (!pills.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2">
      {pills.map((p) => (
        <span
          key={p.key}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
        >
          {p.label}
          <button
            onClick={() => onRemove(p.key)}
            className="ml-0.5 hover:text-blue-900 dark:hover:text-blue-100 transition-colors"
          >
            <X size={11} />
          </button>
        </span>
      ))}
    </div>
  );
};

// ─── Advanced Filter Modal ────────────────────────────────────────────────────

export const AdvancedFilterModal = ({ filters, quickFilterStatus, onApply, onClose }) => {
//   const [local, setLocal] = useState({ ...filters });
  const [local, setLocal] = useState(() => {
    // If the modal has no statuses already set, pre-select the active quick filter
    const display = quickFilterStatus ? mapDisplayStatus(quickFilterStatus) : null;
    const seeded  = !filters.statuses.length && display && ALL_STATUSES.includes(display)
      ? [display]
      : filters.statuses;
    return { ...filters, statuses: seeded };
  });
  const set = (key, val) => setLocal((p) => ({ ...p, [key]: val }));
  const toggleMulti = (key, val) =>
    setLocal((p) => ({
      ...p,
      [key]: p[key].includes(val) ? p[key].filter((v) => v !== val) : [...p[key], val],
    }));
  const activeCount = countActiveFilters(local);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const chipBase   = "px-3 py-1 rounded-full text-xs font-medium border transition-all";
  const chipActive = (color) =>
    `${chipBase} bg-${color}-600 border-${color}-600 text-white`;
  const chipIdle = (color) =>
    `${chipBase} bg-transparent border-gray-300 dark:border-acs-dark-border text-gray-600 dark:text-acs-dark-text hover:border-${color}-400 hover:text-${color}-600`;
  const exceptionChipIdle  = `${chipBase} bg-transparent border-gray-300 dark:border-acs-dark-border text-gray-600 dark:text-acs-dark-text hover:border-red-400 dark:hover:border-red-500 hover:text-red-600 dark:hover:text-red-400`;
  const exceptionChipActive = `${chipBase} bg-red-600 border-red-600 text-white`;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white dark:bg-acs-dark-surface border border-gray-200 dark:border-acs-dark-border rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-acs-dark-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-acs-dark-text">
              Advanced Filters
            </h2>
            {activeCount > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-600 text-white">
                {activeCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-acs-dark-border transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

          <FilterSection>
            {/* Header row: title left, select-all toggle right */}
            <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-custom-bg-blue dark:text-gray-400">
                Status
                </p>

                <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    Select all statuses
                </span>

                {/* Toggle */}
                <button
                    type="button"
                    onClick={() =>
                    set(
                        "statuses",
                        local.statuses.length === ALL_STATUSES.length
                        ? []
                        : [...ALL_STATUSES]
                    )
                    }
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    local.statuses.length === ALL_STATUSES.length
                        ? "bg-blue-600"
                        : "bg-gray-300 dark:bg-gray-600"
                    }`}
                    role="switch"
                    aria-checked={local.statuses.length === ALL_STATUSES.length}
                >
                    <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        local.statuses.length === ALL_STATUSES.length
                        ? "translate-x-5"
                        : "translate-x-0.5"
                    }`}
                    />
                </button>
                </div>
            </div>

            {/* Status chips */}
            <div className="flex flex-wrap gap-2">
                {ALL_STATUSES.map((s) => (
                <button
                    key={s}
                    onClick={() => toggleMulti("statuses", s)}
                    className={
                    local.statuses.includes(s)
                        ? chipActive("blue")
                        : chipIdle("blue")
                    }
                >
                    {s}
                </button>
                ))}
            </div>
          </FilterSection>

          <FilterSection title="Location">
            <div className="grid grid-cols-2 gap-3">
              <LabeledInput
                label="Origin"
                value={local.origin}
                onChange={(v) => set("origin", v)}
                placeholder="e.g. North Valley Logistics Park"
              />
              <LabeledInput
                label="Destination"
                value={local.destination}
                onChange={(v) => set("destination", v)}
                placeholder="e.g. Central Regional Warehouse"
              />
            </div>
          </FilterSection>

          <FilterSection title="Events">
            <div className="flex flex-wrap gap-2">
                {EVENT_OPTIONS.map((s) => (
                <button
                    key={s}
                    onClick={() => toggleMulti("exceptions", s)}
                    className={
                    local.exceptions.includes(s)
                        ? chipActive("blue")
                        : chipIdle("blue")
                    }
                >
                    {s}
                </button>
                ))}
            </div>
          </FilterSection>

          <FilterSection title="Exceptions">
            <div className="flex flex-wrap gap-2">
                {EXCEPTION_OPTIONS.map((s) => (
                <button
                    key={s}
                    onClick={() => toggleMulti("exceptions", s)}
                    className={local.exceptions.includes(s) ? exceptionChipActive : exceptionChipIdle}
                >
                    {s}
                </button>
                ))}
            </div>
          </FilterSection>

          <FilterSection title="Pickup Date Range">
            <DateRangeRow
              label="Planned Pickup"
              fromValue={local.plannedPickupFrom}
              toValue={local.plannedPickupTo}
              onFromChange={(v) => set("plannedPickupFrom", v)}
              onToChange={(v)   => set("plannedPickupTo",   v)}
            />
          </FilterSection>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-acs-dark-border flex-shrink-0 bg-gray-50 dark:bg-acs-dark-surface">
          <button
            onClick={() => setLocal({ ...DEFAULT_ADVANCED_FILTERS })}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          >
            <RotateCcw size={14} /> Reset all
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-acs-dark-border text-gray-700 dark:text-acs-dark-text hover:bg-gray-100 dark:hover:bg-acs-dark-border transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { onApply(local); onClose(); }}
              className="px-5 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
            >
              Apply Filters
              {activeCount > 0 && (
                <span className="ml-1.5 bg-white/25 text-white text-xs rounded-full px-1.5 py-0.5">
                  {activeCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Composite toolbar (button + pills) ──────────────────────────────────────

/**
 * Drop-in "Advanced Filters" toolbar. Renders the toggle button, optional
 * "Clear all" link, and the active filter pills row.
 *
 * Usage:
 *   const { filters, activeCount, updateFilters, clearFilters, removePill }
 *     = useShipmentFilters({ stateKey, persist });
 *
 *   <AdvancedFilterToolbar
 *     filters={filters}
 *     activeCount={activeCount}
 *     onOpenModal={() => setShowModal(true)}
 *     onClearAll={clearFilters}
 *     onRemovePill={removePill}
 *   />
 */
export const AdvancedFilterToolbar = ({
  filters,
  activeCount,
  onOpenModal,
  onClearAll,
  onRemovePill,
  trailing = null,
}) => (
  <>
    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-acs-dark-border flex-shrink-0">
      <div className="flex items-center gap-2">
      <button
        onClick={onOpenModal}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150 ${
          activeCount > 0
            ? "bg-blue-600 border-blue-600 text-white hover:bg-blue-700"
            : "bg-white dark:bg-acs-dark-surface border-gray-300 dark:border-acs-dark-border text-gray-700 dark:text-acs-dark-text hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400"
        }`}
      >
        <SlidersHorizontal size={15} />
        Advanced Filters
        {activeCount > 0 && (
          <span className="ml-0.5 bg-white/25 text-white text-xs font-semibold rounded-full px-1.5 py-0.5 leading-none">
            {activeCount}
          </span>
        )}
      </button>

      {activeCount > 0 && (
        <button
          onClick={onClearAll}
          className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1 transition-colors"
        >
          <RotateCcw size={12} /> Clear all
        </button>
      )}
      </div>
      {trailing}
    </div>

    {activeCount > 0 && (
      <div className="pt-2">
        <ActiveFilterPills filters={filters} onRemove={onRemovePill} />
      </div>
    )}
  </>
);